import React, { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';

import { FORM_SCHEMA } from './constants';
import FormInput from '../FormInput';
import { formatForRender } from '../helpers/format-for-render';
import extractValidationSchema from '../helpers/extract-validation-schema';
import { CONTACT_MUTATION } from '../../services/mutations';

const ContactForm = ({ children }) => {
  const router = useRouter();
  const formSchema = useMemo(() => FORM_SCHEMA, []);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const formFields = useMemo(() => formatForRender(formSchema), [formSchema]);
  const validationSchema = useMemo(() => extractValidationSchema(formSchema), [formSchema]);
  const resolver = useMemo(
    () => ajvResolver(validationSchema, { allErrors: true, strict: true, $data: true }),
    [validationSchema]
  );

  const defaultValues = useMemo(() => {
    const values = {};

    formFields.forEach(fieldGroup => {
      fieldGroup.fields.forEach(field => {
        values[field.fieldId] = field.value || '';
      });
    });

    return values;
  }, [formFields]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({ resolver, mode: 'onSubmit', reValidateMode: 'onChange', defaultValues });

  const [createContact, { loading }] = useMutation(CONTACT_MUTATION);

  const handleSubmitForm = formValues => {
    createContact({
      variables: {
        input: {
          ...formValues,
          urlSentFrom: router.asPath
        }
      },
      onCompleted: () => {
        if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
          window.dataLayer.push({
            event: 'formSubmission',
            formType: 'hal-form',
            formData: formValues
          });
        }
        reset(defaultValues);
        setHasSubmitted(true);
        setErrorMessage(null);
      },
      onError: err => setErrorMessage(err.message)
    });
  };

  if (hasSubmitted) {
    return <div className="form-container">{children}</div>;
  }

  return (
    <div className="form-container">
      <form className="form-container__form" onSubmit={handleSubmit(handleSubmitForm)} noValidate>
        {formFields.map(fieldGroup => (
          <div
            className="form-container__input_group"
            key={fieldGroup.section || fieldGroup.fieldId || JSON.stringify(fieldGroup)}>
            {Array(fieldGroup).map(fields =>
              fields.fields.map(field => {
                if (!field.showInForm) return null;

                return (
                  <FormInput
                    key={field.fieldId}
                    id={field.fieldId}
                    label={field.label}
                    isRequired={field.isRequired}
                    formFieldType={field.formFieldType}
                    register={register}
                    placeholder={field.placeholder}
                    error={errors[field.fieldId]}
                    options={field.options}
                  />
                );
              })
            )}
          </div>
        ))}

        <button
          className="button button--center form__submit-button"
          type="submit"
          disabled={loading}>
          Submit
        </button>

        {errorMessage && (
          <div className="form error_message_wrapper">
            <div className="error_message_icon" />
            <div className="error_message">{errorMessage}</div>
          </div>
        )}
      </form>
    </div>
  );
};

export default ContactForm;
